// Renderer di blocchi: gestisce sia i blocchi "system" (sezioni del sito) sia i
// blocchi "content" (testo/immagine/video/iframe/banner/gallery) creati dall'utente.
import React from 'react';
import { Link } from 'react-router-dom';
import Hero from './Hero';
import PatrociniBar from './PatrociniBar';
import VideoSection from './VideoSection';
import CategoriesSection from './CategoriesSection';
import CampiSection from './CampiSection';
import BrochureSection from './BrochureSection';
import BrochureBanner from './BrochureBanner';
import SponsorsSection from './SponsorsSection';
import FinaleBanner from './FinaleBanner';
import ProgrammaSection from './ProgrammaSection';
import ConcorsoDisegno from './ConcorsoDisegno';
import { PAGE_SYSTEM_COMPONENTS } from './PageSystemBlocks';
import { RichBlock } from './RichBlocks';

const RICH_TYPES = new Set(['heading', 'paragraph', 'button', 'separator', 'spacer', 'quote', 'list', 'card_group', 'columns', 'accordion', 'html', 'gallery_carousel']);

// Mappa system-block → componente
const SYSTEM_COMPONENTS = {
  system_hero: Hero,
  system_patrocini_bar: PatrociniBar,
  system_video: VideoSection,
  system_categories: CategoriesSection,
  system_campi: CampiSection,
  system_finale_banner: FinaleBanner,
  system_programma: ProgrammaSection,
  system_concorso: ConcorsoDisegno,
  system_brochure_banner: BrochureBanner,
  system_brochure: BrochureSection,
  system_sponsors: SponsorsSection,
  ...PAGE_SYSTEM_COMPONENTS,
};

function ContentBlock({ block }) {
  const t = block.type;
  if (t === 'text') {
    const html = simpleMarkdown(block.content || '');
    return (
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-white/85 leading-relaxed prose prose-invert" dangerouslySetInnerHTML={{ __html: html }} />
      </section>
    );
  }
  if (t === 'image') {
    if (!block.src) return null;
    return (
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <img src={block.src} alt={block.alt || ''} className="w-full rounded-2xl" />
          {block.caption && <p className="text-center text-white/55 text-sm mt-2 italic">{block.caption}</p>}
        </div>
      </section>
    );
  }
  if (t === 'video') {
    if (!block.src) return null;
    const isYoutube = /youtu\.?be/.test(block.src);
    return (
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          {isYoutube ? (
            <div className="aspect-video rounded-2xl overflow-hidden bg-black">
              <iframe src={`https://www.youtube.com/embed/${ytId(block.src)}`} title={block.caption || 'video'} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            </div>
          ) : (
            <video src={block.src} controls className="w-full rounded-2xl bg-black" />
          )}
        </div>
      </section>
    );
  }
  if (t === 'iframe') {
    if (!block.src) return null;
    return (
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="rounded-2xl overflow-hidden bg-black" style={{ height: block.height || 480 }}>
            <iframe src={block.src} title={block.caption || 'embed'} className="w-full h-full" allow="fullscreen" />
          </div>
        </div>
      </section>
    );
  }
  if (t === 'banner') {
    return (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div
            className="relative rounded-2xl overflow-hidden p-8 lg:p-14 text-center"
            style={{
              backgroundImage: block.bg ? `url(${block.bg})` : 'linear-gradient(135deg, var(--theme-primary), var(--theme-accent))',
              backgroundSize: 'cover', backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-navy-dark/55" />
            <div className="relative z-10">
              {block.title && <h2 className="font-display text-3xl lg:text-5xl font-bold uppercase text-white">{block.title}</h2>}
              {block.subtitle && <p className="text-white/85 mt-3 text-base lg:text-lg">{block.subtitle}</p>}
              {block.cta_label && block.cta_url && (
                block.cta_url.startsWith('http') ? (
                  <a href={block.cta_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex mt-5">{block.cta_label}</a>
                ) : (
                  <Link to={block.cta_url} className="btn-primary inline-flex mt-5">{block.cta_label}</Link>
                )
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
  if (t === 'gallery') {
    const items = block.items || [];
    if (items.length === 0) return null;
    return (
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((it, idx) => (
              <a key={idx} href={it.src} target="_blank" rel="noreferrer" className="block group">
                <img src={it.src} alt={it.alt || ''} className="w-full aspect-square object-cover rounded-xl group-hover:scale-[1.02] transition-transform" />
              </a>
            ))}
          </div>
        </div>
      </section>
    );
  }
  return null;
}

export default function LayoutRenderer({ blocks = [] }) {
  return (
    <>
      {blocks
        .filter((b) => b && (b.visible !== false))
        .map((b, i) => {
          const Comp = SYSTEM_COMPONENTS[b.type];
          if (Comp) return <Comp key={b.id || `s-${i}`} {...(b.props || {})} />;
          if (RICH_TYPES.has(b.type)) return <RichBlock key={b.id || `r-${i}`} block={b} />;
          return <ContentBlock key={b.id || `c-${i}`} block={b} />;
        })}
    </>
  );
}

export { SYSTEM_COMPONENTS };

function simpleMarkdown(s) {
  const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escapeHtml(s).split(/\n{2,}/).map((p) => {
    let out = p
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*(?!\*)([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-pink-brand underline">$1</a>')
      .replace(/\n/g, '<br/>');
    return `<p>${out}</p>`;
  }).join('');
}

function ytId(url) {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{6,})/);
  return m ? m[1] : '';
}
